import React, { useEffect, useState } from "react";

import {
  Button,
  Col,
  Form,
  Modal,
  Row,
  Table,
  Offcanvas,
} from "react-bootstrap";

import { CardComponent, InputEdit, SelectEdit } from "components";

import { useUsersApi, useAuth } from "utils/hooks";
import { X, Plus } from "react-bootstrap-icons";

import { validateEmail } from "utils/helpers/validators";

const EditModal = ({ user, groupsWithRoles, onSave, onDelete }) => {
  const [userDetails, setUserDetails] = useState(
    user || { email_address: "", roles: [] },
  );

  const [initialUserDetails, setInitialUserDetails] = useState(
    user || { email_address: "", roles: [] },
  );

  const {
    getAuthorizationByRoles,
    getAuthorizationByGroup,
    verifyAuthorization,
  } = useAuth();

  const [groups, setGroups] = useState([]); // Stores an array of group-role pairs
  const [newGroup, setNewGroup] = useState(""); // State for the selected group
  const [newRole, setNewRole] = useState(""); // State for the selected role

  const [errorMessages, setErrorMessage] = useState({
    email: null,
  });

  useEffect(() => {
    user && setUserDetails(user);
    user && setInitialUserDetails(user);
  }, [user]);

  useEffect(() => {
    setGroups(filterGroupsByRole(["Administrator"]) || []);
    setNewGroup(filterGroupsByRole(["Administrator"])[0] || "");
    setNewRole(
      filterGroupsByRole(["Administrator"])[0]?.availableRoles[0] || "",
    );
  }, [groupsWithRoles]);

  const handleRemoveFromGroup = (groupId, roleId) => {
    // Filter out the group-role pair from the groups array
    const updatedGroups = userDetails.groups.filter(
      (group) => group.groupId !== groupId || group.roleId !== roleId,
    );
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      groups: updatedGroups,
    }));
  };

  const filterGroupsByRole = (role) => {
    return groupsWithRoles.filter((group) =>
      getAuthorizationByRoles(role)
        .map((group1) => group1.groupName)
        .includes(group.groupName),
    );
  };

  const handleAddToGroup = () => {
    const groupExists = userDetails.groups.find(
      (group) => group.groupId === newGroup.groupId,
    );

    if (groupExists) {
      // If the group exists, update the role
      const updatedGroups = userDetails.groups.map((group) =>
        group.groupId === newGroup.groupId
          ? { ...group, roleName: newRole.roleName, roleId: newRole.roleId } // Update the role for the existing group
          : group,
      );
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        groups: updatedGroups,
      }));
    } else {
      // If the group doesn't exist, add a new group-role pair
      const updatedGroups = [
        ...userDetails.groups,
        {
          groupId: newGroup.groupId,
          groupName: newGroup.groupName,
          roleName: newRole.roleName,
          roleId: newRole.roleId,
        },
      ];
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        groups: updatedGroups,
      }));
    }
  };

  return (
    <>
      <Row>
        <Form onSubmit={(e) => e.preventDefault()}>
          <Form.Group as={Col} className="mb-1" controlId="formProjectDate">
            <InputEdit
              label={"First name"}
              disabled={true}
              value={user?.first_name || ""}
              onChange={(e) => {
                setUserDetails({
                  ...userDetails,
                  first_name: e.target.value,
                });
              }}
              onKeyDown={(e) => {
                // Prevent numbers from being entered
                if (!/^[a-zA-Z\s]*$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Group>
          <Form.Group as={Col} className="mb-1" controlId="formProjectDate">
            <InputEdit
              label={"Last name"}
              disabled={true}
              value={user?.last_name || ""}
              onChange={(e) => {
                setUserDetails({
                  ...userDetails,
                  last_name: e.target.value,
                });
              }}
              onKeyDown={(e) => {
                // Prevent numbers from being entered
                if (!/^[a-zA-Z\s]*$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
          </Form.Group>
          <Form.Group as={Col} className="mb-1" controlId="formProjectDate">
            <InputEdit
              label={"Email"}
              value={user?.email_address || ""}
              disabled={user ? true : false}
              cleanWhitespaces={true}
              onFocus={(e) => {
                setErrorMessage({
                  ...errorMessages,
                  email: null,
                });
              }}
              onBlur={(e) => {
                if (e.target.value === null || e.target.value === "") {
                  setErrorMessage({
                    ...errorMessages,
                    sisHostname: "cannot be empty",
                  });
                  return;
                }
                !validateEmail(e.target.value) &&
                  setErrorMessage({
                    ...errorMessages,
                    sisHostname: "invalid format",
                  });
              }}
              onChange={(e) => {
                setUserDetails({
                  ...userDetails,
                  email_address: e.target.value,
                });
              }}
            />
          </Form.Group>
          {groups.length > 0 ? (
            <>
              <Form.Group as={Row} className="mb-0" controlId="formProjectDate">
                <Form.Label className={"m-0"} style={{ color: "#dbdbdb" }}>
                  Available networks and roles
                </Form.Label>
              </Form.Group>

              <Form.Group as={Row} className="mb-0" controlId="formProjectDate">
                <Col sm={5}>
                  <SelectEdit
                    size={"sm"}
                    onChange={(e) => {
                      setNewGroup(
                        groups.find(
                          (group) => group.groupId === e.target.value,
                        ),
                      );
                    }}
                  >
                    {groups.map((group) => {
                      return (
                        <option key={group.groupId} value={group.groupId}>
                          {group.groupName}
                        </option>
                      );
                    })}
                  </SelectEdit>
                </Col>
                <Col sm={5}>
                  <SelectEdit
                    size={"sm"}
                    onChange={(e) => {
                      setNewRole(
                        groups
                          .find((group) => group.groupId === newGroup.groupId)
                          ?.availableRoles?.find(
                            (role) => role.roleId === Number(e.target.value),
                          ),
                      );
                    }}
                  >
                    {groups
                      .find((group) => group.groupId === newGroup.groupId)
                      ?.availableRoles?.map((role) => {
                        return (
                          <option key={role.roleId} value={role.roleId}>
                            {role.roleName}
                          </option>
                        );
                      })}
                  </SelectEdit>
                </Col>
                <Col sm={1} className="d-flex align-items-center">
                  <Button
                    style={{
                      width: "28px",
                      height: "28px",
                      padding: "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "auto",
                      // verticalAlign:"center"
                    }}
                    size={"sm"}
                    onClick={handleAddToGroup}
                  >
                    <Plus size={28} />
                  </Button>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-0" controlId="formProjectDate">
                <Form.Label className={"m-0"} style={{ color: "#dbdbdb" }}>
                  Assigned networks with roles
                </Form.Label>
              </Form.Group>
              <Form.Group as={Col} className="mb-4" controlId="formProjectDate">
                <Form.Group
                  as={Col}
                  className="mb-0"
                  controlId="formProjectDate"
                >
                  {userDetails.groups.length === 0 && (
                    <span className="text-warning">
                      <small>
                        <b>Warning:</b> User is not assigned to any network
                      </small>
                    </span>
                  )}
                  {userDetails?.groups?.map((userGroup, index) => {
                    return (
                      <span
                        key={index}
                        style={{ height: 24 }}
                        className="rounded-pill badge bg-primary ms-1 mt-2"
                      >
                        {userGroup.roleName} in {userGroup.groupName}
                        {getAuthorizationByGroup(userGroup.groupName).includes(
                          "Administrator",
                        ) && (
                          <X
                            size={16}
                            style={{
                              cursor: "pointer",
                              borderRadius: "50%",
                              background: "red",
                              color: "white",
                              // padding: "2px",
                              marginLeft: "8px",
                            }}
                            onClick={() =>
                              handleRemoveFromGroup(
                                userGroup.groupId,
                                userGroup.roleId,
                              )
                            }
                          />
                        )}
                      </span>
                    );
                  })}
                </Form.Group>
              </Form.Group>
            </>
          ) : (
            <Form.Group as={Col} className="mb-4" controlId="formProjectDate">
              <small className="text-warning">
                * No networks available. To add a new one{" "}
                <a
                  href="/settings/networks"
                  className="text-decoration-underline text-warning"
                >
                  click here
                </a>
              </small>
            </Form.Group>
          )}
        </Form>
      </Row>

      <div>
        {verifyAuthorization(["Tenant Owner"]) && (
          <Button
            className="align-start"
            variant="outline-danger"
            onClick={() => {
              onDelete(userDetails.userId, userDetails.email_address);
            }}
          >
            {`Delete user`}
          </Button>
        )}

        <Button
          variant="outline-success"
          onClick={() => {
            // user ? onSave(userDetails) : onAdd(userDetails);
            const groupChanges = {
              email_address: userDetails.email_address,
              user_id: userDetails.userId,
              added: [],
              updated: [],
              removed: [],
            };
            // Added or updated groups
            userDetails.groups.forEach((group) => {
              const initialGroup = initialUserDetails.groups.find(
                (g) =>
                  g.groupId === group.groupId &&
                  g.groupName === group.groupName,
              );

              if (!initialGroup) {
                groupChanges.added.push({
                  groupId: group.groupId,
                  groupName: group.groupName,
                  roleName: group.roleName,
                });
              } else if (initialGroup.roleName !== group.roleName) {
                groupChanges.updated.push({
                  groupId: group.groupId,
                  groupName: group.groupName,
                  roleName: group.roleName,
                });
              }
            });

            // Removed groups
            initialUserDetails.groups.forEach((initialGroup) => {
              const stillExists = userDetails.groups.find(
                (g) =>
                  g.groupId === initialGroup.groupId &&
                  g.groupName === initialGroup.groupName,
              );

              if (!stillExists) {
                groupChanges.removed.push({
                  groupId: initialGroup.groupId,
                  groupName: initialGroup.groupName,
                  roleName: initialGroup.roleName,
                });
              }
            });

            user && onSave(groupChanges);
          }}
        >
          {user && `Save Changes`}
        </Button>
      </div>
    </>
  );
};

const AddModal = ({ show, groupsWithRoles, onClose, onAdd }) => {
  const { getAuthorizationByRoles } = useAuth();
  const [userDetails, setUserDetails] = useState({
    username: "",
    email_address: "",
    password: "secret",
    first_name: "",
    last_name: "",
    groups: [],
  });

  const [errorMessages, setErrorMessage] = useState({
    email: null,
  });

  const [groups, setGroups] = useState([]); // Stores an array of group-role pairs
  const [newGroup, setNewGroup] = useState(""); // State for the selected group
  const [newRole, setNewRole] = useState(""); // State for the selected role

  useEffect(() => {
    setGroups(filterGroupsByRoles(["Administrator"]) || []);
    setNewGroup(filterGroupsByRoles(["Administrator"])[0] || {});
    setNewRole(
      filterGroupsByRoles(["Administrator"])[0]?.availableRoles[0] || "",
    );
  }, [groupsWithRoles]);

  const filterGroupsByRoles = (roles) => {
    const authorizedGroupNames = roles
      .flatMap((role) => getAuthorizationByRoles(role))
      .map((group) => group.groupName);

    return groupsWithRoles.filter((group) =>
      authorizedGroupNames.includes(group.groupName),
    );
  };

  const handleRemoveFromGroup = (groupId, roleId) => {
    // Filter out the group-role pair from the groups array
    const updatedGroups = userDetails.groups.filter(
      (group) => group.groupId !== groupId || group.roleId !== roleId,
    );
    setUserDetails((prevDetails) => ({
      ...prevDetails,
      groups: updatedGroups,
    }));
  };

  const handleAddToGroup = () => {
    const groupExists = userDetails.groups.find(
      (group) => group.groupId === newGroup.groupId,
    );

    if (groupExists) {
      // If the group exists, update the role
      const updatedGroups = userDetails.groups.map((group) =>
        group.groupId === newGroup.groupId
          ? { ...group, roleId: newRole.roleId } // Update the role for the existing group
          : group,
      );
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        groups: updatedGroups,
      }));
    } else {
      // If the group doesn't exist, add a new group-role pair
      const updatedGroups = [
        ...userDetails.groups,
        { groupId: newGroup.groupId, roleId: newRole.roleId },
      ];
      setUserDetails((prevDetails) => ({
        ...prevDetails,
        groups: updatedGroups,
      }));
    }
  };

  return (
    <Row>
      <Form onSubmit={(e) => e.preventDefault()}>
        <Form.Group as={Col} className="mb-4" controlId="formProjectDate">
          <InputEdit
            label={"Email"}
            size={"sm"}
            value={userDetails.email_address}
            error={errorMessages.email}
            cleanWhitespaces={true}
            onFocus={(e) => {
              setErrorMessage({
                ...errorMessages,
                email: null,
              });
            }}
            onBlur={(e) => {
              if (e.target.value === null || e.target.value === "") {
                setErrorMessage({
                  ...errorMessages,
                  email: "cannot be empty",
                });
                return;
              }
              !validateEmail(e.target.value) &&
                setErrorMessage({
                  ...errorMessages,
                  email: "invalid format",
                });
            }}
            onChange={(e) => {
              setUserDetails({
                ...userDetails,
                email_address: e.target.value,
              });
            }}
          />
        </Form.Group>
        {groups.length > 0 ? (
          <>
            <Form.Group as={Col} className="mb-4" controlId="formProjectDate">
              <Form.Label className={"m-0"} style={{ color: "#dbdbdb" }}>
                Select a network and a role
              </Form.Label>
              <Form.Group as={Row} className="mb-0" controlId="formProjectDate">
                <Col sm={5}>
                  <SelectEdit
                    size={"sm"}
                    onChange={(e) => {
                      setNewGroup(
                        groups.find(
                          (group) => group.groupId === e.target.value,
                        ),
                      );
                    }}
                  >
                    {groups.map((group) => {
                      return (
                        <option key={group.groupId} value={group.groupId}>
                          {group.groupName}
                        </option>
                      );
                    })}
                  </SelectEdit>
                </Col>
                <Col sm={5}>
                  <SelectEdit
                    size={"sm"}
                    onChange={(e) => {
                      setNewRole(
                        groups
                          .find((group) => group.groupId === newGroup.groupId)
                          ?.availableRoles?.find(
                            (role) => role.roleId === Number(e.target.value),
                          ),
                      );
                    }}
                  >
                    {groups
                      .find((group) => group.groupId === newGroup.groupId)
                      ?.availableRoles?.map((role) => {
                        return (
                          <option key={role.roleId} value={role.roleId}>
                            {role.roleName}
                          </option>
                        );
                      })}
                  </SelectEdit>
                </Col>
                <Col sm={1} className="d-flex align-items-center">
                  <Button
                    style={{
                      width: "28px",
                      height: "28px",
                      padding: "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "auto",
                      // verticalAlign:"center"
                    }}
                    size={"sm"}
                    onClick={handleAddToGroup}
                  >
                    <Plus size={28} />
                  </Button>
                </Col>
              </Form.Group>
            </Form.Group>

            <Form.Group as={Col} className="mb-4" controlId="formProjectDate">
              <Form.Label className={"m-0"} style={{ color: "#dbdbdb" }}>
                Assigned networks with roles
              </Form.Label>
              <Form.Group as={Col} className="mb-0" controlId="formProjectDate">
                {userDetails.groups.length === 0 && (
                  <span className="text-warning">
                    <small>
                      <b>Warning:</b> User is not assigned to any network
                    </small>
                  </span>
                )}
                {userDetails?.groups?.map((userGroup) => {
                  return (
                    <span className="rounded-pill badge bg-primary ms-1 mt-2">
                      {
                        groups
                          ?.find((group) => group.groupId === userGroup.groupId)
                          ?.availableRoles.find(
                            (role) => role.roleId === userGroup.roleId,
                          )?.roleName
                      }{" "}
                      in{" "}
                      {
                        groups?.find(
                          (group) => group.groupId === userGroup.groupId,
                        )?.groupName
                      }
                      <X
                        size={16}
                        style={{
                          cursor: "pointer",
                          borderRadius: "50%",
                          background: "red",
                          color: "white",
                          // padding: "2px",
                          marginLeft: "8px",
                        }}
                        onClick={() =>
                          handleRemoveFromGroup(
                            userGroup.groupId,
                            userGroup.roleId,
                          )
                        }
                      />
                    </span>
                  );
                })}
              </Form.Group>
            </Form.Group>
          </>
        ) : (
          <Form.Group as={Col} className="mb-4" controlId="formProjectDate">
            <small className="text-warning">
              * No networks available. To add a new one{" "}
              <a
                href="/settings/networks"
                className="text-decoration-underline text-warning"
              >
                click here
              </a>
            </small>
          </Form.Group>
        )}
      </Form>

      <Row>
        <Col className="d-flex justify-content-end">
          <Button
            variant="success"
            size="sm"
            disabled={
              Object.values(errorMessages).some((value) => value !== null) ||
              userDetails.email_address === ""
            }
            onClick={() => onAdd(userDetails)}
          >
            Invite user
          </Button>
        </Col>
      </Row>
    </Row>
  );
};

export const UsersManagement = () => {
  const api = useUsersApi();

  const [isFilterActive, setFilterActive] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [groupByValue, setGroupByValue] = useState([]);
  const [editUserId, setEditUserId] = useState(null);

  const filterProperties = ["email_address", "first_name", "last_name"];

  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);

  const [usersDetails, setUserDetails] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);

  const handleClose = () => {
    setShowEditModal(false);
    setShowAddModal(false);
  };

  const handleSave = async (userDetails) => {
    if (userDetails.added.length > 0) {
      await Promise.all(
        userDetails.added.map((group) => {
          return addUserToGroup(
            userDetails.email_address,
            group.groupId,
            group.groupName,
            group.roleName,
          );
        }),
      );
    }

    if (userDetails.updated.length > 0) {
      await Promise.all(
        userDetails.updated.map((group) => {
          return updateUserRoleInGroup(
            userDetails.email_address,
            group.groupId,
            userDetails.user_id,
            group.groupName,
            group.roleName,
          );
        }),
      );
    }

    // Remove users from groups in parallel
    if (userDetails.removed.length > 0) {
      await Promise.all(
        userDetails.removed.map((group) => {
          return removeUserFromGroup(
            userDetails.email_address,
            group.groupId,
            group.groupName,
            group.roleName,
          );
        }),
      );
    }

    getAllUsersDetails();
    setShowEditModal(false);
    setShowAddModal(false);
  };
  const handleAdd = (userDetails) => {
    sendUserInvite(userDetails);
    setShowEditModal(false);
    setShowAddModal(false);
  };
  const handleDelete = (userId, userEmail) => {
    deleteUser(userId, userEmail);
    setUserDetails(
      usersDetails?.filter((user) => user.email_address !== userEmail),
    );
    setShowEditModal(false);
  };

  const getAllUsersDetails = async () => {
    try {
      const response = await api.getUsers();

      setUserDetails(response?.data);
    } catch (e) {
      // throw e;
    }
  };

  const getGroups = async () => {
    try {
      const response = await api.getGroups();
      setAvailableGroups(response?.data);
    } catch (e) {
      // throw e;
    }
  };

  const updateUsersDetails = async (userDetails) => {
    try {
      const response = await api.updateUserDetails(
        userDetails.userId,
        userDetails,
      );
    } catch (e) {
      // throw e;
    }
  };

  const addUserToGroup = async (
    email_address,
    groupId,
    groupName,
    roleName,
  ) => {
    try {
      const response = await api.addUserToGroup(groupId, {
        email_address: email_address,
        group_name: groupName,
        role: roleName,
      });
    } catch (e) {
      // throw e;
    }
  };

  const removeUserFromGroup = async (
    email_address,
    groupId,
    groupName,
    roleName,
  ) => {
    try {
      const response = await api.removeUserFromGroup(groupId, {
        email_address: email_address,
        group_name: groupName,
        role: roleName,
      });
    } catch (e) {
      // throw e;
    }
  };

  const updateUserRoleInGroup = async (
    email_address,
    groupId,
    userId,
    groupName,
    roleName,
  ) => {
    try {
      const response = await api.updateUserInGroup(groupId, userId, {
        email_address: email_address,
        group_name: groupName,
        role: roleName,
      });
    } catch (e) {
      // throw e;
    }
  };

  const sendUserInvite = async (userDetails) => {
    try {
      const response = await api.sendInvitation(userDetails);
    } catch (e) {
      // throw e;
    }
  };

  const deleteUser = async (userId) => {
    try {
      const response = await api.deleteUserById(userId);
    } catch (e) {
      // throw e;
    }
  };

  useEffect(() => {
    getAllUsersDetails();

    getGroups();
  }, [api]);

  return (
    <>
      <Row className="m-3">
        <CardComponent title={"Users"}>
          <Form.Group as={Row} className="ps-1 pe-1 pt-0 pb-2">
            <Col>
              <InputEdit
                type={"search"}
                placeholder={"Search"}
                disableLabel
                onChange={(e) => {
                  if (e.target.value) {
                    setFilterActive(true);
                    setFilterValue(e.target.value);
                  } else {
                    setFilterActive(false);
                    setFilterValue("");
                  }
                }}
              />
            </Col>
            <Col className="d-flex justify-content-end">
              <Button
                onClick={() => {
                  setEditUserId(null);
                  setShowAddModal(true);
                }}
              >
                Invite user
              </Button>
            </Col>
          </Form.Group>
          <Table responsive="xl">
            <thead>
              <tr>
                <th>#</th>
                <th>First name</th>
                <th>Last name</th>
                <th>Email</th>
                <th>Networks</th>
                <th>Roles</th>
                <th>Is active</th>
                <th>Last login</th>
                <th>Date of registration</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {usersDetails
                ?.filter((user) => {
                  return (
                    (!isFilterActive ||
                      filterProperties.some((prop) =>
                        user[prop]
                          ?.toLowerCase()
                          .includes(filterValue.toLowerCase()),
                      )) &&
                    (groupByValue.length === 0 ||
                      groupByValue.includes(user.roles))
                  );
                })
                .map((user, index) => (
                  <tr key={user.userId}>
                    <td>{index + 1}</td>
                    <td>{user.first_name ?? "-"}</td>
                    <td>{user.last_name ?? "-"}</td>
                    <td>{user.email_address ?? "-"}</td>
                    <td>
                      {user.groups && user.groups.length > 0 ? (
                        <>
                          {user.groups.slice(0, 2).map((group) => (
                            <div key={`${user.userId}-${group.groupId}`}>
                              {group.groupName}
                            </div>
                          ))}
                          {user.groups.length > 2 && (
                            <a
                              href="#"
                              onClick={() => {
                                setEditUserId(user.email_address);
                                setShowEditModal(true);
                              }}
                            >
                              {user.groups.length - 2} more
                            </a>
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {user.groups && user.groups.length > 0 ? (
                        <>
                          {user.groups.slice(0, 2).map((group) => (
                            <div key={`${user.userId}-${group.groupId}-role`}>
                              {group.roleName}
                            </div>
                          ))}
                          {user.groups.length > 2 && (
                            <a
                              href="#"
                              onClick={() => {
                                setEditUserId(user.email_address);
                                setShowEditModal(true);
                              }}
                            >
                              {user.groups.length - 2} more
                            </a>
                          )}
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td
                      className={user.active ? "text-success" : "text-danger"}
                    >
                      {user.active ? "active" : "inactive"}
                    </td>
                    <td>{user.lastSeen}</td>
                    <td>
                      {user.createdAt
                        ? new Date(user.createdAt).toISOString()
                        : "-"}
                    </td>
                    <td className="d-flex justify-content-end">
                      <Button
                        variant="outline-warning"
                        size="sm"
                        className="me-1"
                        onClick={() => {
                          setEditUserId(user.email_address);
                          setShowEditModal(true);
                        }}
                      >
                        Edit user
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </CardComponent>
      </Row>
      <Offcanvas
        style={{
          borderLeft: "0.5pt solid #dbdbdb",
          backgroundColor: "#141414",
        }}
        show={showAddModal}
        onHide={handleClose}
        placement="end"
      >
        <Offcanvas.Header>
          <Offcanvas.Title style={{ color: "#dbdbdb" }}>
            Invite User
          </Offcanvas.Title>
          <button
            type="button"
            className="close"
            style={{
              color: "#dbdbdb",
              border: "none",
              background: "none",
              fontSize: "1.5rem",
            }}
            onClick={handleClose}
          >
            &times;
          </button>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <AddModal
            groupsWithRoles={availableGroups}
            onClose={handleClose}
            onAdd={handleAdd}
          />
        </Offcanvas.Body>
      </Offcanvas>

      <Offcanvas
        style={{
          borderLeft: "0.5pt solid #dbdbdb",
          backgroundColor: "#141414",
        }}
        show={showEditModal}
        onHide={handleClose}
        placement="end"
      >
        <Offcanvas.Header>
          <Offcanvas.Title style={{ color: "#dbdbdb" }}>
            Edit User
          </Offcanvas.Title>
          <button
            type="button"
            className="close"
            style={{
              color: "#dbdbdb",
              border: "none",
              background: "none",
              fontSize: "1.5rem",
            }}
            onClick={handleClose}
          >
            &times;
          </button>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <EditModal
            user={usersDetails.find(
              (user) => user.email_address === editUserId,
            )}
            groupsWithRoles={availableGroups}
            onSave={handleSave}
            onDelete={handleDelete}
          />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};
